/* Message metadata helpers.
 *
 * CONTRACT MISMATCH, handled here rather than in models.ts (which other
 * features own): `types.Message.Metadata` is a Go `string` holding a JSON blob
 * — both in Firestore and on the REST response — while `Message.metadata` is
 * declared as `Record<string, unknown>`. Everything that reaches a card in this
 * feature has already been through `parseMetadata`, so the declared type is
 * true by the time it is read; these helpers stay tolerant of the raw string
 * anyway, because `getMessageHistory()` (the no-realtime fallback) would hand
 * back the unparsed form.
 *
 * The same blob is also where `quoteId` and `appointmentId` actually live: the
 * Go Message struct has no top-level field for either, so a card that trusted
 * `message.quoteId` alone would find nothing.
 */

export function parseMetadata(value: unknown): Record<string, unknown> | undefined {
  if (!value) return undefined;
  if (typeof value === "object") return value as Record<string, unknown>;
  if (typeof value !== "string") return undefined;
  try {
    const parsed: unknown = JSON.parse(value);
    return parsed && typeof parsed === "object" ? (parsed as Record<string, unknown>) : undefined;
  } catch {
    // A malformed blob must degrade to "no extras", never take the thread down.
    return undefined;
  }
}

export function metaString(metadata: unknown, key: string): string | undefined {
  const parsed = parseMetadata(metadata);
  const value = parsed?.[key];
  return typeof value === "string" && value ? value : undefined;
}

export function metaNumber(metadata: unknown, key: string): number | undefined {
  const parsed = parseMetadata(metadata);
  const value = parsed?.[key];
  if (typeof value === "number" && !Number.isNaN(value)) return value;
  if (typeof value === "string" && value.trim() !== "") {
    const asNumber = Number(value);
    return Number.isNaN(asNumber) ? undefined : asNumber;
  }
  return undefined;
}
